/* eslint-disable no-underscore-dangle */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { ServiceModal, type ServiceModalState } from '../ServiceModal/ServiceModal'
import { ServiceRoles } from '../ServiceRoles/ServiceRoles'

import { Button, Card, Flex, Heading, Tabs } from '@percent/lemonade'
import { TabPanel } from '@percent/admin-dashboard/common/components'
import type { InternalServiceWithPermissions } from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'
import { ServiceOverview } from './ServiceOverview'

const overviewRoute = (serviceId: string) => `/accounts/services/${serviceId}`
const rolesRoute = (serviceId: string) => `/accounts/services/${serviceId}/roles`

type ServiceDetailProps = {
  service: InternalServiceWithPermissions
  /** After the service or a role on it changes, so what the viewer may do is read again. */
  onChanged: () => void
}

export function ServiceDetail({ service, onChanged }: Readonly<ServiceDetailProps>) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [modal, setModal] = useState<ServiceModalState>(null)
  const permissions = service._permissions

  if (pathname === rolesRoute(service.id) && !permissions.can_view_roles) {
    return <Navigate to={overviewRoute(service.id)} replace />
  }

  const tabs = [
    { key: overviewRoute(service.id), label: t('serviceDetail.tabs.overview') },
    ...(permissions.can_view_roles ? [{ key: rolesRoute(service.id), label: t('serviceDetail.tabs.roles') }] : [])
  ]

  return (
    <Card>
      <Flex direction="column" w="100%">
        <Flex direction="row" justify="space-between" align="center" px="lg" pt="lg" pb="md">
          <Heading level="h5" data-testid="service-detail-name">
            {service.name}
          </Heading>
          <Flex direction="row" gap="sm">
            {permissions.can_edit && (
              <Button
                variant="secondary"
                onClick={() => setModal({ kind: 'edit', service })}
                data-testid="service-detail-edit-button"
              >
                {t('button.edit')}
              </Button>
            )}
            {permissions.can_delete && (
              <Button
                variant="secondary"
                onClick={() => setModal({ kind: 'delete', service })}
                data-testid="service-detail-delete-button"
              >
                {t('button.delete')}
              </Button>
            )}
          </Flex>
        </Flex>
        <Flex direction="column" px="lg">
          <Tabs activeTab={pathname} onChangeTab={navigate} items={tabs} />
        </Flex>
        <TabPanel value={pathname} index={overviewRoute(service.id)}>
          <ServiceOverview service={service} />
        </TabPanel>
        {permissions.can_view_roles && (
          <TabPanel value={pathname} index={rolesRoute(service.id)}>
            <Flex direction="column" p="lg">
              <ServiceRoles service={service} canSetRoles={permissions.can_set_roles} onChanged={onChanged} />
            </Flex>
          </TabPanel>
        )}
      </Flex>
      <ServiceModal
        modal={modal}
        onClose={() => setModal(null)}
        onChanged={onChanged}
        onDeleted={() => navigate('/accounts/services')}
      />
    </Card>
  )
}
